/*
 * Root-level `config/` sits outside `src`, like `lib/`, so it has no alias. The file is the
 * source of truth for whoppah/sitemaps as well; see the comment inside it.
 */
import policy from "../../../../config/seo-index-policy.json";
import type { PageInfo } from "@/features/search/api/types";

/** The `filters.terms` of the rules a listing page ran with: `{ brand: ["vitra"], category: ["sofas"] }`. */
export type PageRuleTerms = Record<string, string[]>;

/** Live products a brand x category or designer x category page needs to be indexed and in the sitemap. */
export const BRAND_CATEGORY_MIN_ACTIVE_PRODUCTS: number =
  policy.brandCategoryMinActiveProducts;

const normalizePath = (path: string) => path.replace(/^\/+|\/+$/g, "");

/**
 * Every localized service path of every whitelisted facet page, so a lookup needs no locale: a
 * whitelisted page is whitelisted in all six locales, or its hreflang cluster would point at
 * `noindex` alternates.
 */
const WHITELISTED_FACET_PATHS: ReadonlySet<string> = new Set(
  policy.facetWhitelist.flatMap(entry =>
    Object.values(entry.paths).flatMap(path =>
      path ? [normalizePath(path)] : []
    )
  )
);

/**
 * A brand x category or designer x category page: the service ran it with a `brand` or
 * `designer` rule *and* a `category` rule. `/brands/vitra` alone (no category) is a brand hub
 * and not subject to the publish floor.
 */
export const isBrandOrDesignerCategoryPage = (
  terms: PageRuleTerms | null | undefined
) =>
  terms != null &&
  ("brand" in terms || "designer" in terms) &&
  "category" in terms;

/**
 * Whether a brand x category page has too few live products to be published.
 *
 * Any brand x category combination that ever had a product exists as a URL, and Search Console
 * (2026-09-15) files them under "Discovered, currently not indexed" at scale. The recovery plan
 * (2.2) publishes such a page only from `BRAND_CATEGORY_MIN_ACTIVE_PRODUCTS` live products; below
 * that it is `noindex,follow` here and absent from the sitemap (whoppah/sitemaps reads the same
 * constant). A missing response (a failed search) is unknown, not thin, and stays indexable.
 */
export const isBelowPublishFloor = (
  terms: PageRuleTerms | null | undefined,
  pageInfo: Pick<PageInfo, "total"> | null | undefined
) =>
  isBrandOrDesignerCategoryPage(terms) &&
  pageInfo != null &&
  pageInfo.total < BRAND_CATEGORY_MIN_ACTIVE_PRODUCTS;

/** A material or colour listing page (`/furniture/sofas/leather`, `/nl/meubels/kasten/hout`). */
export const isFacetPage = (terms: PageRuleTerms | null | undefined) =>
  terms != null && ("material" in terms || "color" in terms);

/** Whether a service path (`furniture/sofas/leather`, with or without slashes) is on the facet whitelist. */
export const isWhitelistedFacetPath = (path: string | null | undefined) =>
  path != null && WHITELISTED_FACET_PATHS.has(normalizePath(path));

/**
 * Whether a facet page is outside the whitelist and so `noindex,follow`.
 *
 * Every category has a material and a colour page per value, thousands of URLs that mostly repeat
 * the category page with a filter applied. The recovery plan (2.3) keeps the small set with real
 * search demand indexable (`config/seo-index-policy.json`, derived from the frozen GSC pull) and
 * takes the rest out of the index; their links keep counting. The whitelist is consulted on the
 * page's own service URL, which is what the sitemap generator sees too.
 */
export const isUnlistedFacetPage = (
  terms: PageRuleTerms | null | undefined,
  pageUrl: string | null | undefined
) => isFacetPage(terms) && !isWhitelistedFacetPath(pageUrl);
